import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  accountTransactionsApi,
  type PostManualAccountTransactionRequest,
} from "@/lib/clients-accounts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DetailModal } from "@/components/detail-modal";
import { toast } from "@/hooks/use-toast";

// ---------------------------------------------------------------------------
// Adjust (manual credit/debit/write-off) dialog
// ---------------------------------------------------------------------------

// Labels map to the EXACT tranTypeCode values accepted by the manual-post
// endpoint (EfAccountBalanceService.ManualTypes / the FluentValidation rule):
// only "ADJ" (debit adjustment) and "WO" (credit write-off) are accepted.
// "CreditAdjustment" / "DebitAdjustment" / "Refund" are separate Plan II
// ledger-only types not postable through this manual endpoint.
type AdjustTypeCode = "ADJ" | "WO";

interface AdjustTypeOption {
  code: AdjustTypeCode;
  label: string;
}

const ADJUST_TYPE_OPTIONS: AdjustTypeOption[] = [
  { code: "ADJ", label: "Debit Adjustment" },
  { code: "WO", label: "Write-off (Credit)" },
];

export function AdjustAccountDialog({
  open,
  onOpenChange,
  accountId,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  accountId: number;
}) {
  const qc = useQueryClient();
  const [tranTypeCode, setTranTypeCode] = useState("");
  const [amount, setAmount] = useState("");
  const [transactionDate, setTransactionDate] = useState(new Date().toISOString().slice(0, 10));
  const [comment, setComment] = useState("");

  function resetForm() {
    setTranTypeCode("");
    setAmount("");
    setTransactionDate(new Date().toISOString().slice(0, 10));
    setComment("");
  }

  const adjustMut = useMutation({
    mutationFn: (body: PostManualAccountTransactionRequest) =>
      accountTransactionsApi.post(accountId, body),
    onSuccess: () => {
      toast.success("Transaction posted");
      void qc.invalidateQueries({ queryKey: ["account", accountId] });
      void qc.invalidateQueries({ queryKey: ["account-tx", accountId] });
      onOpenChange(false);
      resetForm();
    },
    onError: () => toast.error("Failed to post transaction"),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!tranTypeCode || !amount) return;
    adjustMut.mutate({
      tranTypeCode,
      amount: Number(amount),
      transactionDate: new Date(transactionDate).toISOString(),
      comment: comment || null,
    });
  }

  return (
    <DetailModal open={open} onOpenChange={onOpenChange} title="Adjust account">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="adj-type">Type *</Label>
          <Select value={tranTypeCode} onValueChange={setTranTypeCode}>
            <SelectTrigger id="adj-type">
              <SelectValue placeholder="Select adjustment type" />
            </SelectTrigger>
            <SelectContent>
              {ADJUST_TYPE_OPTIONS.map((o) => (
                <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="adj-amount">Amount *</Label>
          <Input
            id="adj-amount"
            type="number"
            step="0.01"
            min="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
        </div>
        <div>
          <Label htmlFor="adj-date">Date *</Label>
          <Input
            id="adj-date"
            type="date"
            value={transactionDate}
            onChange={(e) => setTransactionDate(e.target.value)}
            required
          />
        </div>
        <div>
          <Label htmlFor="adj-comment">Comment</Label>
          <Input id="adj-comment" value={comment} onChange={(e) => setComment(e.target.value)} />
        </div>
        <Button type="submit" disabled={adjustMut.isPending || !tranTypeCode || !amount}>
          {adjustMut.isPending ? "Saving…" : "Save"}
        </Button>
      </form>
    </DetailModal>
  );
}
